import { getApiBaseUrl } from '../lib/api';
import React, { useState, useEffect, useCallback } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { 
  ArrowLeft, RefreshCw, MapPin, Calendar, Clock, User, 
  Phone, Mail, CheckCircle2, Play, Pause, Package,
  MessageSquare, Send, FileText, PoundSterling, Camera,
  AlertCircle, Wrench, Building2
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = getApiBaseUrl();

const ContractorJobDetails = () => {
  const { jobId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [job, setJob] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [sendingMessage, setSendingMessage] = useState(false);
  const [actionLoading, setActionLoading] = useState(null);
  
  // Schedule modal state
  const [showSchedule, setShowSchedule] = useState(false);
  const [scheduleDate, setScheduleDate] = useState('');
  const [scheduleTime, setScheduleTime] = useState('');
  
  // Complete modal state
  const [showComplete, setShowComplete] = useState(false);
  const [completionNotes, setCompletionNotes] = useState('');
  const [finalAmount, setFinalAmount] = useState('');

  const getToken = () => localStorage.getItem('access_token') || localStorage.getItem('token');

  const fetchJob = useCallback(async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/contractors/jobs/${jobId}`, {
        headers: { 'Authorization': `Bearer ${getToken()}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setJob(data);
        setMessages(data.messages || []);
      } else {
        toast.error('Failed to load job details');
        navigate('/contractor/dashboard');
      }
    } catch (error) {
      console.error('Error fetching job:', error);
      toast.error('Failed to load job details');
    } finally {
      setLoading(false);
    }
  }, [jobId, navigate]);

  useEffect(() => {
    fetchJob();
  }, [fetchJob]);

  const handleAction = async (action, body = {}) => {
    setActionLoading(action);
    try {
      const response = await fetch(`${API_URL}/api/contractors/jobs/${jobId}/${action}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${getToken()}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(body)
      });
      
      if (response.ok) {
        toast.success(`Job ${action} successful`);
        fetchJob();
        setShowSchedule(false);
        setShowComplete(false);
      } else {
        const error = await response.json();
        toast.error(error.detail || `Failed to ${action} job`);
      }
    } catch (error) {
      toast.error(`Failed to ${action} job`);
    } finally {
      setActionLoading(null);
    }
  };

  const handleSchedule = () => {
    if (!scheduleDate) {
      toast.error('Please select a date');
      return;
    }
    handleAction('schedule', {
      scheduled_date: scheduleDate,
      time_slot: scheduleTime,
    });
  };

  const handleComplete = () => {
    if (!completionNotes || !finalAmount) {
      toast.error('Please fill in all fields');
      return;
    }
    handleAction('complete', {
      completion_notes: completionNotes,
      final_amount: parseFloat(finalAmount),
    });
  };

  const handleSendMessage = async () => {
    if (!newMessage.trim()) return;
    
    setSendingMessage(true);
    try {
      const response = await fetch(`${API_URL}/api/contractors/jobs/${jobId}/messages`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${getToken()}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ content: newMessage })
      });
      
      if (response.ok) {
        setNewMessage('');
        fetchJob();
      } else {
        toast.error('Failed to send message');
      }
    } catch (error) {
      toast.error('Failed to send message');
    } finally {
      setSendingMessage(false);
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      pending: 'bg-amber-100 text-amber-700',
      accepted: 'bg-blue-100 text-blue-700',
      scheduled: 'bg-purple-100 text-purple-700',
      in_progress: 'bg-indigo-100 text-indigo-700',
      awaiting_parts: 'bg-orange-100 text-orange-700',
      completed: 'bg-green-100 text-green-700',
      declined: 'bg-red-100 text-red-700',
    };
    return colors[status] || 'bg-slate-100 text-slate-700';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <RefreshCw className="h-8 w-8 animate-spin text-blue-600" />
      </div>
    );
  }

  if (!job) {
    return (
      <div className="p-8 text-center">
        <AlertCircle className="h-12 w-12 mx-auto text-amber-500 mb-4" />
        <h2 className="text-xl font-semibold">Job not found</h2>
        <Link to="/contractor/dashboard">
          <Button className="mt-4">Back to Dashboard</Button>
        </Link>
      </div>
    );
  }

  const canSchedule = ['accepted'].includes(job.status);
  const canStart = ['accepted', 'scheduled'].includes(job.status);
  const canComplete = ['accepted', 'scheduled', 'in_progress', 'awaiting_parts'].includes(job.status);
  const canUpdateStatus = ['in_progress'].includes(job.status);

  return (
    <div className="min-h-screen bg-slate-50 p-4 md:p-8" data-testid="contractor-job-details">
      {/* Header */}
      <div className="mb-6">
        <Link to="/contractor/jobs/active" className="inline-flex items-center text-blue-600 hover:text-blue-700 mb-4">
          <ArrowLeft className="h-4 w-4 mr-1" />
          Back to Jobs
        </Link>
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">
              {job.maintenance?.title || 'Maintenance Job'}
            </h1>
            <div className="flex items-center gap-2 mt-2">
              <Badge className={getStatusColor(job.status)}>
                {job.status?.replace('_', ' ')}
              </Badge>
              {job.maintenance?.category && (
                <Badge variant="outline">{job.maintenance.category}</Badge>
              )}
              {job.maintenance?.priority && (
                <Badge variant={job.maintenance.priority === 'emergency' ? 'destructive' : 'outline'}>
                  {job.maintenance.priority}
                </Badge>
              )}
            </div>
          </div>
          <Button variant="outline" onClick={fetchJob}>
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Content */}
        <div className="lg:col-span-2 space-y-6">
          {/* Job Actions */}
          <Card>
            <CardHeader>
              <CardTitle>Job Actions</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex flex-wrap gap-3">
                {canSchedule && (
                  <Button onClick={() => setShowSchedule(true)} variant="outline">
                    <Calendar className="h-4 w-4 mr-2" />
                    Schedule
                  </Button>
                )}
                {canStart && (
                  <Button 
                    onClick={() => handleAction('start')}
                    disabled={actionLoading === 'start'}
                    className="bg-blue-600"
                  >
                    {actionLoading === 'start' ? (
                      <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                    ) : (
                      <Play className="h-4 w-4 mr-2" />
                    )}
                    Start Job
                  </Button>
                )}
                {canUpdateStatus && (
                  <Button 
                    onClick={() => handleAction('status', { status: 'awaiting_parts' })}
                    variant="outline"
                    disabled={actionLoading === 'status'}
                  >
                    <Package className="h-4 w-4 mr-2" />
                    Awaiting Parts
                  </Button>
                )}
                {canComplete && (
                  <Button 
                    onClick={() => setShowComplete(true)}
                    className="bg-green-600 hover:bg-green-700"
                  >
                    <CheckCircle2 className="h-4 w-4 mr-2" />
                    Mark Complete
                  </Button>
                )}
              </div>

              {/* Schedule Modal */}
              {showSchedule && (
                <div className="mt-4 p-4 border rounded-lg bg-slate-50">
                  <h4 className="font-medium mb-3">Schedule Job</h4>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="text-sm text-slate-600">Date</label>
                      <Input
                        type="date"
                        value={scheduleDate}
                        onChange={(e) => setScheduleDate(e.target.value)}
                      />
                    </div>
                    <div>
                      <label className="text-sm text-slate-600">Time Slot</label>
                      <Input
                        placeholder="e.g., 09:00-12:00"
                        value={scheduleTime}
                        onChange={(e) => setScheduleTime(e.target.value)}
                      />
                    </div>
                  </div>
                  <div className="flex gap-2 mt-3">
                    <Button onClick={handleSchedule} disabled={actionLoading === 'schedule'}>
                      {actionLoading === 'schedule' ? 'Scheduling...' : 'Confirm Schedule'}
                    </Button>
                    <Button variant="ghost" onClick={() => setShowSchedule(false)}>Cancel</Button>
                  </div>
                </div>
              )}

              {/* Complete Modal */}
              {showComplete && (
                <div className="mt-4 p-4 border rounded-lg bg-slate-50">
                  <h4 className="font-medium mb-3">Complete Job</h4>
                  <div className="space-y-3">
                    <div>
                      <label className="text-sm text-slate-600">Completion Notes *</label>
                      <Textarea
                        placeholder="Describe the work completed..."
                        value={completionNotes}
                        onChange={(e) => setCompletionNotes(e.target.value)}
                        rows={3}
                      />
                    </div>
                    <div>
                      <label className="text-sm text-slate-600">Final Amount (£) *</label>
                      <Input
                        type="number"
                        step="0.01"
                        placeholder="0.00"
                        value={finalAmount}
                        onChange={(e) => setFinalAmount(e.target.value)}
                      />
                    </div>
                  </div>
                  <div className="flex gap-2 mt-3">
                    <Button 
                      onClick={handleComplete} 
                      disabled={actionLoading === 'complete'}
                      className="bg-green-600"
                    >
                      {actionLoading === 'complete' ? 'Completing...' : 'Complete Job'}
                    </Button>
                    <Button variant="ghost" onClick={() => setShowComplete(false)}>Cancel</Button>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Job Details */}
          <Card>
            <CardHeader>
              <CardTitle>Job Description</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-slate-700 whitespace-pre-wrap">
                {job.maintenance?.description || job.description || 'No description provided'}
              </p>
              
              {job.maintenance?.access_instructions && (
                <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                  <p className="text-sm font-medium text-amber-700">Access Instructions:</p>
                  <p className="text-sm text-amber-600">{job.maintenance.access_instructions}</p>
                </div>
              )}

              {/* Photos */}
              {job.maintenance?.photos && job.maintenance.photos.length > 0 && (
                <div className="mt-4">
                  <p className="text-sm font-medium text-slate-600 mb-2">Photos:</p>
                  <div className="flex flex-wrap gap-2">
                    {job.maintenance.photos.map((photo, idx) => (
                      <div key={idx} className="w-24 h-24 rounded-lg overflow-hidden bg-slate-100">
                        <img 
                          src={photo.url || photo} 
                          alt={`Job photo ${idx + 1}`} 
                          className="w-full h-full object-cover"
                        />
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Messages */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <MessageSquare className="h-5 w-5" />
                Messages
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3 max-h-80 overflow-y-auto mb-4">
                {messages.length === 0 ? (
                  <p className="text-sm text-slate-500 text-center py-4">No messages yet</p>
                ) : (
                  messages.map((msg) => (
                    <div 
                      key={msg.id}
                      className={`p-3 rounded-lg ${
                        msg.sender_type === 'contractor' 
                          ? 'bg-blue-50 ml-8' 
                          : msg.sender_type === 'system' || msg.sender_type === 'ai_agent'
                            ? 'bg-slate-100 text-center'
                            : 'bg-white border mr-8'
                      }`}
                    >
                      {msg.sender_type !== 'system' && (
                        <p className="text-xs text-slate-500 mb-1">
                          {msg.sender_name || msg.sender_type} • {new Date(msg.created_at).toLocaleString('en-GB')}
                        </p>
                      )}
                      <p className="text-sm">{msg.content}</p>
                    </div>
                  ))
                )}
              </div>

              {/* Message Input */}
              <div className="flex gap-2">
                <Input
                  placeholder="Type a message..."
                  value={newMessage}
                  onChange={(e) => setNewMessage(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handleSendMessage()}
                />
                <Button 
                  onClick={handleSendMessage}
                  disabled={sendingMessage || !newMessage.trim()}
                >
                  {sendingMessage ? <RefreshCw className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Property Info */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Building2 className="h-5 w-5" />
                Property
              </CardTitle>
            </CardHeader>
            <CardContent>
              {job.property ? (
                <div className="space-y-2 text-sm">
                  <p className="font-medium">{job.property.address}</p>
                  <div className="flex items-center gap-2 text-slate-500">
                    <MapPin className="h-4 w-4" />
                    {job.property.postcode}
                  </div>
                </div>
              ) : (
                <p className="text-sm text-slate-500">Property details not available</p>
              )}
            </CardContent>
          </Card>

          {/* Schedule Info */}
          {(job.scheduled_date || job.accepted_at) && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Calendar className="h-5 w-5" />
                  Schedule
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-2 text-sm">
                {job.scheduled_date && (
                  <div className="flex items-center gap-2">
                    <Calendar className="h-4 w-4 text-slate-400" />
                    <span>{new Date(job.scheduled_date).toLocaleDateString('en-GB', { 
                      weekday: 'long', 
                      year: 'numeric', 
                      month: 'long', 
                      day: 'numeric' 
                    })}</span>
                  </div>
                )}
                {job.accepted_at && (
                  <div className="flex items-center gap-2 text-slate-500">
                    <Clock className="h-4 w-4" />
                    <span>Accepted: {new Date(job.accepted_at).toLocaleDateString('en-GB')}</span>
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* Costs */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <PoundSterling className="h-5 w-5" />
                Costs
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-2 text-sm">
              {job.maintenance?.estimated_cost && (
                <div className="flex justify-between">
                  <span className="text-slate-500">Estimated:</span>
                  <span>£{job.maintenance.estimated_cost}</span>
                </div>
              )}
              {job.quoted_amount && (
                <div className="flex justify-between">
                  <span className="text-slate-500">Quoted:</span>
                  <span>£{job.quoted_amount}</span>
                </div>
              )}
              {job.final_amount && (
                <div className="flex justify-between font-medium">
                  <span>Final:</span>
                  <span>£{job.final_amount}</span>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Quotes */}
          {job.quotes && job.quotes.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <FileText className="h-5 w-5" />
                  Quotes
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {job.quotes.map((quote) => (
                    <div key={quote.id} className="p-2 border rounded text-sm">
                      <div className="flex justify-between">
                        <span>{quote.quote_type}</span>
                        <Badge variant={quote.status === 'approved' ? 'default' : 'outline'}>
                          {quote.status}
                        </Badge>
                      </div>
                      <p className="font-medium mt-1">£{quote.total_amount}</p>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
};

export default ContractorJobDetails;
